import axios from 'axios'
import { allconfig } from '../configs/config'

const baseURL = allconfig.env === 'dev' ? allconfig.dev_api_url : allconfig.prod_api_url

const api = axios.create({
  baseURL: baseURL + '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
})

/** POST paths where 401 means invalid credentials, not an expired session */
const AUTH_ATTEMPT_PATHS = new Set(['/auth/student/login', '/auth/admin/login'])

function normalizeRequestPath(config) {
  const raw = (config?.url || '').split('?')[0]
  if (!raw) return ''
  return raw.startsWith('/') ? raw : `/${raw}`
}

function isAuthAttempt401(config) {
  const method = (config?.method || 'get').toLowerCase()
  if (method !== 'post') return false
  return AUTH_ATTEMPT_PATHS.has(normalizeRequestPath(config))
}

let authSessionRedirectScheduled = false

api.interceptors.response.use(
  (res) => res,
  (error) => {
    const { response, config } = error
    if (response?.status !== 401 || !config || config.skipAuthRedirect) {
      return Promise.reject(error)
    }
    if (isAuthAttempt401(config)) {
      return Promise.reject(error)
    }

    const path = normalizeRequestPath(config)
    const isAdminResource = path.startsWith('/admin/')

    if (isAdminResource) {
      if (authSessionRedirectScheduled) return Promise.reject(error)
      authSessionRedirectScheduled = true
      window.location.assign(`${window.location.origin}/admin`)
      return Promise.reject(error)
    }

    if (localStorage.getItem('isAuthenticated') !== 'true') {
      return Promise.reject(error)
    }

    if (authSessionRedirectScheduled) return Promise.reject(error)
    authSessionRedirectScheduled = true
    localStorage.removeItem('isAuthenticated')
    localStorage.removeItem('user')
    window.location.assign(`${window.location.origin}/login`)
    return Promise.reject(error)
  }
)

export const topicsApi = {
  generate: (body) => api.post('/topics/generate', body),
  list: (params) => api.get('/topics', { params }),
  get: (id) => api.get(`/topics/${id}`),
  create: (body) => api.post('/topics', body)
}

export const projectsApi = {
  list: (params) => api.get('/projects', { params }),
  get: (id) => api.get(`/projects/${id}`),
  create: (body) => api.post('/projects', body),
  update: (id, body) => api.put(`/projects/${id}`, body)
}

export const documentationApi = {
  generate: (body) => api.post('/documentation/generate', body),
  /** SSE stream: progress events then { type: 'complete', data } */
  generateStream: (body, onProgress) => documentationGenerateStream(body, onProgress),
  list: (params) => api.get('/documentation', { params }),
  get: (id) => api.get(`/documentation/${id}`),
  exportPdf: (id) => api.get(`/documentation/${id}/export/pdf`, { responseType: 'blob' }),
  exportDocx: (id) => api.get(`/documentation/${id}/export/docx`, { responseType: 'blob' }),
  regenerate: (id, body = {}) => api.patch(`/documentation/${id}/regenerate`, body),
  regenerateChapter: (docId, chapterId, body = {}) => api.patch(`/documentation/${docId}/chapter/${chapterId}/regenerate`, body),
  updateChapter: (docId, chapterId, body) => api.patch(`/documentation/${docId}/chapter/${chapterId}`, body)
}

/**
 * Paid documentation generation with live progress (Server-Sent Events).
 */
export async function documentationGenerateStream (body, onProgress) {
  const url = `${baseURL}/api/documentation/generate-stream`
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream'
    },
    body: JSON.stringify(body)
  })

  const contentType = res.headers.get('content-type') || ''
  if (!contentType.includes('text/event-stream')) {
    const errJson = await res.json().catch(() => ({}))
    const err = new Error(errJson.message || `Generation failed (${res.status})`)
    err.response = { status: res.status, data: errJson }
    throw err
  }

  const reader = res.body?.getReader()
  if (!reader) {
    throw new Error('No response stream from server')
  }

  const decoder = new TextDecoder()
  let buffer = ''
  let completeData = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const chunks = buffer.split('\n\n')
    buffer = chunks.pop() || ''
    for (const block of chunks) {
      const line = block.split('\n').find((l) => l.startsWith('data: '))
      if (!line) continue
      let evt
      try {
        evt = JSON.parse(line.slice(6))
      } catch {
        continue
      }
      if (evt.type === 'progress' && typeof onProgress === 'function') onProgress(evt)
      if (evt.type === 'complete') completeData = evt.data
      if (evt.type === 'error') {
        const err = new Error(evt.message || 'Generation failed')
        err.code = evt.code
        if (evt.code === 'PAYMENT_REQUIRED') {
          err.response = { status: 402, data: evt }
        }
        throw err
      }
    }
  }

  if (!completeData) {
    throw new Error('Generation stream ended unexpectedly')
  }
  return completeData
}

export const usageApi = {
  get: () => api.get('/usage')
}

export const paymentsApi = {
  initialize: (body) => api.post('/payments/initialize', body),
  mine: (params) => api.get('/payments/mine', { params })
}

export const referralsApi = {
  mine: (params) => api.get('/referrals/mine', { params }),
  generateCode: () => api.post('/referrals/generate-code')
}

export const schoolsApi = {
  list: () => api.get('/schools')
}

const normalizeListData = (response) => {
  const root = response?.data
  const data = root?.data

  if (Array.isArray(data)) return data
  if (Array.isArray(data?.courses)) return data.courses
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(root?.courses)) return root.courses
  if (Array.isArray(root?.items)) return root.items

  return []
}

export const coursesApi = {
  list: async (params = {}) => {
    const response = await api.get('/courses', {
      params
    })

    return {
      ...response,
      data: {
        ...(response?.data || {}),
        data: normalizeListData(response)
      }
    }
  }
}

export const adminApi = {
  payments: { list: (params) => api.get('/admin/payments', { params }) },
  referrals: {
    list: (params) => api.get('/admin/referrals', { params }),
    markPaid: (id, body) => api.post(`/admin/referrals/${id}/mark-paid`, body)
  },
  students: {
    list: (params) => api.get('/admin/students', { params }),
    get: (id) => api.get(`/admin/students/${id}`),
    setStatus: (id, body) => api.put(`/admin/students/${id}/status`, body)
  },
  schools: {
    list: () => api.get('/admin/schools'),
    get: (id) => api.get(`/admin/schools/${id}`),
    create: (body) => api.post('/admin/schools', body),
    update: (id, body) => api.put(`/admin/schools/${id}`, body),
    remove: (id) => api.delete(`/admin/schools/${id}`),
    seedNigeria: () => api.post('/admin/schools/seed-nigeria')
  },
  courses: {
    list: (params) => api.get('/admin/courses', { params }),
    seedMissingTemplates: () => api.post('/admin/courses/seed-missing-templates'),
    get: (id) => api.get(`/admin/courses/${id}`),
    create: (body) => api.post('/admin/courses', body),
    update: (id, body) => api.put(`/admin/courses/${id}`, body),
    remove: (id) => api.delete(`/admin/courses/${id}`)
  },
  ideas: {
    list: (params) => api.get('/admin/ideas', { params }),
    get: (id) => api.get(`/admin/ideas/${id}`)
  },
  documentations: {
    list: (params) => api.get('/admin/documentations', { params }),
    get: (id) => api.get(`/admin/documentations/${id}`),
    exportPdf: (id) => api.get(`/admin/documentations/${id}/export/pdf`, { responseType: 'blob' }),
    exportDocx: (id) => api.get(`/admin/documentations/${id}/export/docx`, { responseType: 'blob' })
  }
}

export const authApi = {
  signup: (body) => api.post('/auth/student/signup', body),
  login: (body) => api.post('/auth/student/login', body),
  requestPasswordReset: (body) => api.post('/auth/student/password/request-reset', body),
  resetPassword: (body) => api.post('/auth/student/password/reset', body),
  me: () => api.get('/auth/student/me'),
  updateProfile: (body) => api.patch('/auth/student/profile', body)
}

export const onboardingApi = {
  skip: () => api.post('/auth/student/onboarding/skip'),
  complete: () => api.post('/auth/student/onboarding/complete')
}

export default api
